import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "antd";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import backBtnMobile from "../../assets/images/backBtnMobile.svg";
import backBtn from "../../assets/images/backBtn.svg";
import { EducationalESign } from "./EducationalComponents";
import EducationalContentHeader from "./EducationalContentHeader";
import EducationalContentContinueButton from "./EducationalContentContinueButton";
import { getEsignContentHandler, getEsignContentSliceAction } from "../../redux/action/guestUserAction/getEsignContent";
import { acceptEsignAgreementHandler, acceptEsignAgreementSliceAction } from "../../redux/action/guestUserAction/acceptEsignAgreement";
import { useDispatch, useSelector } from "react-redux";
import { guestEnrollmentBackPath } from "../../common/guestEnrollmentFlow";
import { STATUS_CODE } from "../../common";
import NoDataAvailableState from "./NoDataAvailableState";
import Loader from "../Loder/loader";

const EducationalContentESign = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [esignValidationError, setEsignValidationError] = useState("");
  const [showAgreementView, setShowAgreementView] = useState(
    Boolean(location.state?.openAgreement),
  );
  const [showPaycheckUpdatedModal, setShowPaycheckUpdatedModal] = useState(false);
  const dispatch = useDispatch();
  const getEsignContentSelector = useSelector((state) => state?.getEsignContent);
  const acceptEsignAgreementSelector = useSelector((state) => state?.acceptEsignAgreement);
  const htmlContent = getEsignContentSelector?.data?.data?.htmlContent;
  const documentUrl = getEsignContentSelector?.data?.data?.documentUrl;
  const isEsignApiSuccess =
    getEsignContentSelector?.data?.statusCode === STATUS_CODE.OK;
  const showNoDataState =
    !getEsignContentSelector?.isLoading &&
    !isEsignApiSuccess;
  useEffect(() => {
    dispatch(getEsignContentHandler());
  }, []);

  useEffect(() => {
    const payCheckStatus =
      getEsignContentSelector?.data?.data?.payCheckStatus ||
      acceptEsignAgreementSelector?.data?.data?.payCheckStatus;
    if (payCheckStatus) {
      setShowPaycheckUpdatedModal(true);
    }
  }, [
    getEsignContentSelector?.data?.data?.payCheckStatus,
    acceptEsignAgreementSelector?.data?.data?.payCheckStatus,
  ]);

  useEffect(() => {
    if (location.state?.enrollmentDecision !== "YES") {
      navigate("/guest-user/enrollment", { replace: true });
    }
  }, [location.state, navigate]);

  const handleBack = () => {
    if (showAgreementView) {
      setShowAgreementView(false);
      return;
    }
    navigate(guestEnrollmentBackPath("esign"));
  };

  const handleContinue = async () => {
    const trimmedSignature = signature?.trim();
    if (!trimmedSignature || !agreed) {
      setEsignValidationError(t("Please enter your full name and accept the checkbox to continue."));
      return;
    }

    setEsignValidationError("");
    try {
      const response = await dispatch(
        acceptEsignAgreementHandler({
          name: trimmedSignature,
          deviceId: "2342351351-135135135",
          platform: "",
        }),
      );
      if (response?.statusCode === 200) {
        if (response?.data?.data?.payCheckStatus) {
          setShowPaycheckUpdatedModal(true);
          return;
        }
        navigate("/guest-user/thank-you", {
          state: {
            enrollmentDecision: "YES",
            signature: trimmedSignature,
            agreed,
          },
        });
      }
    } catch (e) {
      setEsignValidationError(t("Unable to complete enrollment. Please try again."));
      return;
    }


  };

  const handlePaycheckUpdatedOk = () => {
    const payCheckStatus =
      getEsignContentSelector?.data?.data?.payCheckStatus ||
      acceptEsignAgreementSelector?.data?.data?.payCheckStatus;
    setShowPaycheckUpdatedModal(false);
    navigate("/guest-user/paycheck", {
      state: {
        payCheckStatus: payCheckStatus || "PAYCHECK_ILLUSTRATION_VIEWED",
        enrollmentDecision: "YES",
      },
    });
    dispatch(getEsignContentSliceAction.getEsignContentSliceReset());
    dispatch(acceptEsignAgreementSliceAction.acceptEsignAgreementReset());
  };

  return (
    <>
    {acceptEsignAgreementSelector?.isLoading && <Loader /> }
    <EducationalContentHeader
      backBtnSrc={backBtn}
      backBtnMobileSrc={backBtnMobile}
      onBack={handleBack}
      title={t("Participation Agreement")}
      hideNavigation={false}
      stepText={`${t("Step")} 4 ${t("of")} 4`}
      progress="100%"
    >
      <div className="stepperview mb-60">
        {showAgreementView ? (
          <div className="w-100">
            <div className="fs-20 fw-700 blueColor mb-20 tgb">{t("Enrollment Agreement")}</div>
            {documentUrl ? (
              <Worker
                workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js"
              >
                <Viewer fileUrl={documentUrl} />
              </Worker>
            ) : (
              <p className="fs-14 mb-4" style={{ color: "#E80000" }}>
                {t("Enrollment agreement is not available right now.")}
              </p>
            )}
          </div>
        ) : (
          showNoDataState ? (
            <NoDataAvailableState subtitle={t("There is no educational material found")} />
          ) : (
            <EducationalESign
              signature={signature}
              setSignature={setSignature}
              agreed={agreed}
              setAgreed={setAgreed}
              htmlContent={htmlContent}
              documentUrl={documentUrl}
              onViewAgreement={() => setShowAgreementView(true)}
            />
          )
        )}
        {!!esignValidationError && (
          <p className="fs-14 mb-4" style={{ color: "#E80000" }}>
            {esignValidationError}
          </p>
        )}
      </div>
      {!showAgreementView && !showNoDataState && (<EducationalContentContinueButton
        hideNavigation={false}
        onContinue={handleContinue}
        // buttonLabel={t("Activate My Coverage")}
        buttonLabel={t("Activate Your Benefits")}
        disabled={
          showAgreementView
          || !signature?.trim()
          || !agreed
          || acceptEsignAgreementSelector?.isLoading
        }
      />)}

      <Modal
        className="paycheckConfirmationModal"
        centered
        open={showPaycheckUpdatedModal}
        footer={null}
        closeIcon={null}
        maskClosable={false}
        keyboard={false}
      >
        <div className="tgb fs-20 lh-25 mb-40 text-center">
          {t(
            "Your paycheck has been updated. Tap Okay to view your paycheck illustration.",
          )}
        </div>
        <div className="text-center">
          <button
            type="button"
            className="saveBtn cursor-pointer "
            onClick={handlePaycheckUpdatedOk}
          >
            {t("Okay")}
          </button>
        </div>
      </Modal>

    </EducationalContentHeader>
    </>
  );
};

export default EducationalContentESign;
